import {
  Button, Modal, Spacing, SpacingName, TypographyName,
} from '@doveit/lego';
import { formatEuro } from '@doveit/hammer';
import dynamic from 'next/dynamic';
import React, { useCallback, useMemo, useState } from 'react';
import { NextPage } from 'next';
import ArticleMarkdown from '../client/components/article-markdown/ArticleMarkdown';
import { BreadcrumbItem } from '../client/components/breadcrumb/Breadcrumb';
import PageHead, { PageHeadProps } from '../client/components/page-head/PageHead';
import PageSection from '../client/components/page-section/PageSection';
import CreateIntent from '../client/containers/create-web-intent/CreateWebIntent';
import { BUILDING_SITE_QUERY } from '../client/graphql/queries/building-site';
import useNextConfig from '../client/hooks/useNextConfig/useNextConfig';
import ListingLayout from '../client/layouts/listing-layout/ListingLayout';
import { sendEvent } from '../client/services/tracking/tracking';
import * as styles from '../client/templates/building-site/BuildingSite.style';
import DivParagraphRenderer from '../client/templates/building-site/div-paragraph-renderer/DivParagraphRenderer';
import FramedImageRenderer from '../client/templates/building-site/framed-image-renderer/FramedImageRenderer';
import ListItemRenderer from '../client/templates/building-site/list-item-renderer/ListItemRenderer';
import ListRenderer from '../client/templates/building-site/list-renderer/ListRenderer';
import EnergySection from '../client/templates/common/energy-section/EnergySection';
import GallerySection from '../client/templates/common/gallery-section/GallerySection';
import ListingHeader from '../client/templates/common/listing-header/ListingHeader';
import ContactBarMobile from '../client/templates/listing/contact-bar-mobile/ContactBarMobile';
import ListingLocation from '../client/templates/listing/listing-location/ListingLocation';
import { getFeatures, getGallery } from '../client/utils/building-site/buildingSite';
import { assertNotNullOrUndefined } from '../client/utils/error/error';
import { buildNotFound } from '../client/utils/error/http/httpErrors';
import {
  buildBuildingSiteURLObject, BUILDING_SITES_URL, BUILDING_SITE_AS, slugIt, URLObject,
  buildAgentURLObject,
} from '../client/utils/links';
import { template } from '../client/utils/string/string';
import { getBreadcrumbStructuredData, getStructuredData } from '../data/structured';
import * as domain from '../domain/graphcms';
import initGraphQLClient from '../client/graphql/initGraphQLClient';
import { withGetServerSideError, withError } from '../client/hocs/with-server-side-error/withServerSideError';
import { importDynamicData } from '../services/importDynamicData';
import IntentBox from '../client/templates/common/intent-box/IntentBox';
import { AgentCardProps } from '../client/components/agent-card/AgentCard';

export interface BuildingSiteProps {
  SEO: PageHeadProps['SEO'],
  canonical: URLObject,
  buildingSite: Omit<domain.BuildingSite, 'agent'> & { agent: AgentCardProps },
  intentPhone: string,
  form: {
    title: string,
    message: {
      placeholder: string,
    },
  },
}

const ViewProperties = dynamic(() => import('../client/containers/view-properties/ViewProperties'), {
  ssr: false,
});

const MARKDOWN_COMPONENTS = {
  ul: ListRenderer,
  ol: ListRenderer,
  li: ListItemRenderer,
  img: FramedImageRenderer,
  p: DivParagraphRenderer,
};

export const BuildingSite: NextPage<BuildingSiteProps> = ({
  SEO,
  canonical,
  buildingSite,
  intentPhone,
  form,
}) => {
  const { MAPS } = useNextConfig();

  const breadcrumb = useMemo<BreadcrumbItem[]>(() => [
    {
      label: 'Nuove costruzioni',
      urlObject: {
        url: BUILDING_SITES_URL,
        as: BUILDING_SITE_AS,
      },
    },
    {
      label: buildingSite.title,
      urlObject: canonical,
    },
  ], [buildingSite.title, canonical]);

  const [isContactModalOpen, setContactModalOpen] = useState(false);
  const onToggleContactModal = useCallback(() => {
    setContactModalOpen(!isContactModalOpen);
  }, [isContactModalOpen]);

  const side = useCallback(() => (
    <div css={styles.secondaryWrapper}>
      <IntentBox
        reference={`DV-${buildingSite.intentPropertyId}`}
        agent={buildingSite.agent}
        title={form.title}
      >
        <CreateIntent
          id={buildingSite.intentPropertyId}
          onSuccess={(intent) => sendEvent(
            'intent_submit',
            {
              category: 'intent',
              action: 'submit',
              label: 'side:intent-form',
              intentId: intent.id,
            },
          )}
        />
      </IntentBox>
    </div>
  ), [buildingSite.agent, buildingSite.intentPropertyId, form.title]);

  const header = useCallback(() => (
    <ListingHeader
      title={buildingSite.title}
      status={buildingSite.siteStatus}
      subtitle={buildingSite.suburb}
      highlights={getFeatures(buildingSite)}
      logo={buildingSite.logo ? buildingSite.logo.image.url : undefined}
      price={`da ${formatEuro(buildingSite.minPrice)}`}
    />
  ), [buildingSite]);

  return (
    <ListingLayout
      render={{
        side,
        header,
      }}
      activePage="building-site"
      breadcrumb={breadcrumb}
    >
      <PageHead
        SEO={SEO}
        structuredData={getStructuredData(getBreadcrumbStructuredData(breadcrumb))}
      />
      <GallerySection
        title={buildingSite.title}
        normalizedAddress={buildingSite.normalizedAddress}
        images={getGallery(buildingSite)}
        virtualTourSrc={buildingSite.virtualTour}
      />

      {buildingSite.sections.map(({ title, content }) => (
        <PageSection
          key={title}
          padding={[SpacingName.BIG, 0, 0]}
        >
          <PageSection.Wrapper withoutPad>
            <PageSection.Title
              size={TypographyName.H4}
              withBorder
            >
              {title}
            </PageSection.Title>
            <PageSection.Block>
              <ArticleMarkdown
                components={MARKDOWN_COMPONENTS}
              >
                {content}
              </ArticleMarkdown>
            </PageSection.Block>
          </PageSection.Wrapper>
        </PageSection>
      ))}

      <PageSection padding={[SpacingName.BIG, 0, 0]}>
        <PageSection.Wrapper withoutPad>
          <PageSection.Title
            size={TypographyName.H4}
            withBorder
          >
            {buildingSite.propertiesSection.title}
          </PageSection.Title>
          <PageSection.Block>
            <ArticleMarkdown>
              {buildingSite.propertiesSection.content}
            </ArticleMarkdown>

            <ViewProperties ids={buildingSite.propertyIds} />

            <footer>
              <Button
                label="Richiedi maggiori informazioni"
                expanded
                onClick={onToggleContactModal}
              />

              <Modal
                open={isContactModalOpen}
                onCloseHandler={onToggleContactModal}
              >
                <Modal.Close />
                <Modal.Header title="Contattaci" />
                <Modal.Body padded>
                  <CreateIntent
                    id={buildingSite.intentPropertyId}
                    onSuccess={(intent) => sendEvent(
                      'intent_submit',
                      {
                        category: 'intent',
                        action: 'submit',
                        label: 'modal:intent-form',
                        intentId: intent.id,
                      },
                    )}
                  />
                </Modal.Body>
              </Modal>
            </footer>
          </PageSection.Block>
        </PageSection.Wrapper>
      </PageSection>

      <PageSection padding={[SpacingName.BIG, 0, 0]}>
        <PageSection.Wrapper withoutPad>
          <PageSection.Title
            size={TypographyName.H4}
            withBorder
          >
            Efficienza energetica
          </PageSection.Title>
          <PageSection.Block>
            <EnergySection
              energyClass={buildingSite.energyClass}
              energyValue={buildingSite.energyValue}
            />
          </PageSection.Block>
        </PageSection.Wrapper>
      </PageSection>

      <PageSection padding={[SpacingName.BIG, 0]}>
        <PageSection.Wrapper withoutPad>
          <PageSection.Title
            size={TypographyName.H4}
            withBorder
          >
            {buildingSite.location.title}
          </PageSection.Title>

          <PageSection.Block>
            <ArticleMarkdown>
              {buildingSite.location.content}
            </ArticleMarkdown>

            <Spacing margin={[SpacingName.SMALL, 0, 0, 0]}>
              <ListingLocation
                apiKey={MAPS.apiKey}
                route={buildingSite.route}
                normalizedAddress={buildingSite.normalizedAddress}
                suburb={buildingSite.suburb}
              />
            </Spacing>
          </PageSection.Block>
        </PageSection.Wrapper>
      </PageSection>

      <div css={styles.requestInfo}>
        <PageSection padding={[0, 0, SpacingName.BIG, 0]}>
          <PageSection.Wrapper withoutPad>
            <PageSection.Title
              size={TypographyName.H4}
              withBorder
            >
              Richiesta informazioni
            </PageSection.Title>
            <PageSection.Block>
              <IntentBox
                reference={`DV-${buildingSite.intentPropertyId}`}
                agent={buildingSite.agent}
                title={form.title}
                minimal
              >
                <CreateIntent
                  id={buildingSite.intentPropertyId}
                  onSuccess={(intent) => sendEvent(
                    'intent_submit',
                    {
                      category: 'intent',
                      action: 'submit',
                      label: 'main:intent-form',
                      intentId: intent.id,
                    },
                  )}
                />
              </IntentBox>
            </PageSection.Block>
          </PageSection.Wrapper>
        </PageSection>
      </div>

      <ContactBarMobile
        phone={intentPhone}
        emailAction={onToggleContactModal}
      />
    </ListingLayout>
  );
};

export const getServerSideProps = withGetServerSideError<BuildingSiteProps>(async ({ query }) => {
  const { id } = query;
  const graphQLClient = initGraphQLClient();

  const [common, pageData, graphqlResponse] = await Promise.all([
    importDynamicData('common.yml'),
    importDynamicData('pages/building-site.yml'),
    graphQLClient.query({
      query: BUILDING_SITE_QUERY,
      variables: { id },
    }),
  ]);

  const { data: { buildingSite } } = graphqlResponse;
  assertNotNullOrUndefined(buildingSite, buildNotFound(`Building site with id "${id}" not found`));

  const canonical = buildBuildingSiteURLObject({
    id,
    slug: slugIt(buildingSite.title),
    locality: buildingSite.locality.slug,
  });

  const actual = buildBuildingSiteURLObject(query);

  if (canonical.as !== actual.as) {
    return {
      redirect: {
        destination: canonical.as,
        permanent: true,
      },
    };
  }

  const { agent } = buildingSite;
  const agentCardProps = agent ? ({
    name: `${agent.name} ${agent.surname}`,
    jobTitle: agent.jobTitleType.name,
    picture: agent.photo,
    urlObject: agent.biography && buildAgentURLObject({ slug: agent.slug }),
    franchising: agent.franchisingAgencies.length > 0,
  }) : null;

  return {
    props: {
      ...pageData,
      canonical,
      SEO: template(pageData.SEO, {
        route: buildingSite.route,
        locality: buildingSite.locality.name,
      }),
      intentPhone: common.intentPhone,
      buildingSite: {
        ...buildingSite,
        agent: agentCardProps,
      },
    },
  };
});

export default withError(BuildingSite);
